import React from 'react';
import ReactDOM from 'react-dom';
import { FaArrowLeft, FaUser, FaEnvelope, FaHospital, FaCalendarAlt, FaClock, FaShieldAlt, FaTrashAlt, FaUndoAlt } from 'react-icons/fa';
import { toRelativePath } from '../../utils/videoImagePath';
import { useUserDetail } from './useUserDetail';
import { UserDetailProps } from './types';
import LoadingIndicator from '../../components/LoadingIndicator';
import styles from './UserDetail.module.css';

const UserDetail: React.FC<UserDetailProps> = ({ currentUser }) => {
  const {
    user,
    loading,
    error,
    selectedRole,
    isConfirmOpen,
    confirmAction,
    actionInProgress,
    setSelectedRole,
    handleRoleChange,
    handleDeleteUser,
    handleRestoreUser,
    openConfirmDialog,
    closeConfirmDialog,
    getHospitalName,
    formatDate,
    navigateToUsers,
    isCurrentUser,
    isAdmin
  } = useUserDetail(currentUser);

  // ConfirmModalをPortalを使ってレンダリングするコンポーネント
  const ConfirmModal = () => {
    if (!isConfirmOpen || !user) return null;
    
    return ReactDOM.createPortal(
      <div 
        className={styles['user-detail-page-modal-backdrop']}
        data-testid="user-detail-page__modal-backdrop"
      >
        <div 
          className={styles['user-detail-page-confirm-modal']}
          data-testid="user-detail-page__confirm-modal"
        >
          <h3>
            {confirmAction === 'delete' ? 'ユーザーを削除' : 'ユーザーを復元'}
          </h3>
          <p>
            {confirmAction === 'delete' 
              ? `${user.name} を削除してもよろしいですか？このユーザーは論理削除され、後から復元することができます。`
              : `${user.name} を復元してもよろしいですか？このユーザーは再び有効になります。`
            }
          </p>
          <div className={styles['user-detail-page-modal-actions']}>
            <button 
              className={styles['user-detail-page-cancel-btn']}
              onClick={closeConfirmDialog}
              disabled={actionInProgress}
              data-testid="user-detail-page__cancel-button"
            >
              キャンセル
            </button>
            <button 
              className={confirmAction === 'delete' ? 
                styles['user-detail-page-delete-btn'] : 
                styles['user-detail-page-restore-btn']
              }
              onClick={confirmAction === 'delete' ? handleDeleteUser : handleRestoreUser}
              disabled={actionInProgress}
              data-testid={confirmAction === 'delete' ? 
                "user-detail-page__confirm-delete-button" : 
                "user-detail-page__confirm-restore-button"
              }
            >
              {actionInProgress 
                ? '処理中...' 
                : confirmAction === 'delete' ? '削除する' : '復元する'
              }
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  if (loading) {
    return (
      <div className={styles['user-detail-page-loading-container']}>
        <LoadingIndicator
          className={styles['user-detail-page-loading-indicator']}
          message="データを読み込み中..."
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles['user-detail-page-container']}>
        <div className={styles['user-detail-page-header']}>
          <h1 className={styles['user-detail-page-title']}>ユーザ詳細</h1>
        </div>
        <div className={styles['user-detail-page-error-container']}>
          <p className={styles['user-detail-page-error-message']}>{error}</p>
          <button 
            className={styles['user-detail-page-primary-button']}
            onClick={navigateToUsers}
            data-testid="user-detail-page__back-to-list-button"
          >
            ユーザ一覧に戻る
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className={styles['user-detail-page-container']}>
        <div className={styles['user-detail-page-header']}>
          <h1 className={styles['user-detail-page-title']}>ユーザ詳細</h1>
        </div>
        <div className={styles['user-detail-page-error-container']}>
          <p className={styles['user-detail-page-error-message']}>ユーザー情報の取得に失敗しました</p>
          <button 
            className={styles['user-detail-page-primary-button']}
            onClick={navigateToUsers}
            data-testid="user-detail-page__back-to-list-button"
          >
            ユーザ一覧に戻る
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles['user-detail-page-container']} mobile-page-content`} data-testid="user-detail-page">
      <div className={styles['user-detail-page-header']}>
        <h1 className={styles['user-detail-page-title']}>ユーザ詳細</h1>
      </div>

      <button 
        className={styles['user-detail-page-back-button']}
        onClick={navigateToUsers}
        data-testid="user-detail-page__back-button"
      >
        <FaArrowLeft /> ユーザ一覧に戻る
      </button>
      
      <div className={styles['user-detail-page-content']}>
        <div className={styles['user-detail-page-card']}>
          <div className={styles['user-detail-page-user-header']}>
            <div className={styles['user-detail-page-user-avatar']}>
              {user.icon_path ? (
                <img 
                  src={toRelativePath(user.icon_path)}
                  alt={`${user.name}のアイコン`}
                  data-testid="user-detail-page__user-avatar"
                />
              ) : (
                <div 
                  className={styles['user-detail-page-avatar-placeholder']}
                  data-testid="user-detail-page__avatar-placeholder"
                >
                  {user.name.charAt(0)}
                </div>
              )}
            </div>
            <div className={styles['user-detail-page-user-main-info']}>
              <h2 
                className={styles['user-detail-page-user-name']}
                data-testid="user-detail-page__user-name"
              >
                {user.name}
              </h2>
              <div className={styles['user-detail-page-user-status']}>
                <span className={user.deleted_at ? styles['user-detail-page-deleted-status'] : styles['user-detail-page-active-status']}
                  data-testid="user-detail-page__user-status"
                >
                  {user.deleted_at ? '削除済み' : '有効'}
                </span>
              </div>
            </div>
          </div>
          
          <div className={styles['user-detail-page-user-body']}>
            <div className={styles['user-detail-page-info-section']}>
              <h3>基本情報</h3>
              <div className={styles['user-detail-page-info-row']}>
                <div className={styles['user-detail-page-info-label']}>
                  <FaUser /> 名前
                </div>
                <div 
                  className={styles['user-detail-page-info-value']}
                  data-testid="user-detail-page__info-name"
                >
                  {user.name}
                </div>
              </div>
              <div className={styles['user-detail-page-info-row']}>
                <div className={styles['user-detail-page-info-label']}>
                  <FaEnvelope /> メールアドレス
                </div>
                <div 
                  className={styles['user-detail-page-info-value']}
                  data-testid="user-detail-page__info-email"
                >
                  {user.email}
                </div>
              </div>
              <div className={styles['user-detail-page-info-row']}>
                <div className={styles['user-detail-page-info-label']}>
                  <FaHospital /> 所属病院
                </div>
                <div 
                  className={styles['user-detail-page-info-value']}
                  data-testid="user-detail-page__info-hospital"
                >
                  {getHospitalName(user.hospital_id)}
                </div>
              </div>
              <div className={styles['user-detail-page-info-row']}>
                <div className={styles['user-detail-page-info-label']}>
                  <FaShieldAlt /> 権限
                </div>
                <div 
                  className={styles['user-detail-page-info-value']}
                  data-testid="user-detail-page__info-role"
                >
                  {isAdmin && !isCurrentUser && !user.deleted_at ? (
                    <div className={styles['user-detail-page-role-control']}>
                      <select
                        value={selectedRole}
                        onChange={(e) => setSelectedRole(e.target.value)}
                        disabled={actionInProgress}
                        className={styles['user-detail-page-role-select']}
                        data-testid="user-detail-page__role-select"
                      >
                        <option value="user">ユーザー</option>
                        <option value="admin">管理者</option>
                      </select>
                      {selectedRole !== user.role && (
                        <button 
                          className={styles['user-detail-page-save-role-btn']}
                          onClick={handleRoleChange}
                          disabled={actionInProgress}
                          data-testid="user-detail-page__save-role-button"
                        >
                          保存
                        </button>
                      )}
                    </div>
                  ) : (
                    <span>{user.role === 'admin' ? '管理者' : 'ユーザー'}</span>
                  )}
                </div>
              </div>
            </div>
            
            <div className={styles['user-detail-page-info-section']}>
              <h3>日時情報</h3>
              <div className={styles['user-detail-page-info-row']}>
                <div className={styles['user-detail-page-info-label']}>
                  <FaCalendarAlt /> 登録日時
                </div>
                <div 
                  className={styles['user-detail-page-info-value']}
                  data-testid="user-detail-page__info-created-at"
                >
                  {formatDate(user.created_at)}
                </div>
              </div>
              <div className={styles['user-detail-page-info-row']}>
                <div className={styles['user-detail-page-info-label']}>
                  <FaClock /> 最終更新日時
                </div>
                <div 
                  className={styles['user-detail-page-info-value']}
                  data-testid="user-detail-page__info-updated-at"
                >
                  {formatDate(user.updated_at)}
                </div>
              </div>
              {user.deleted_at && (
                <div className={styles['user-detail-page-info-row']}>
                  <div className={styles['user-detail-page-info-label']}>
                    <FaTrashAlt /> 削除日時
                  </div>
                  <div 
                    className={styles['user-detail-page-info-value']}
                    data-testid="user-detail-page__info-deleted-at"
                  >
                    {formatDate(user.deleted_at)}
                  </div>
                </div>
              )}
            </div>
            
            {isAdmin && !isCurrentUser && (
              <div className={styles['user-detail-page-info-section']}>
                <h3>ユーザーアクション</h3>
                <div className={styles['user-detail-page-action-buttons']}>
                  {!user.deleted_at ? (
                    <button 
                      className={styles['user-detail-page-delete-button']}
                      onClick={() => openConfirmDialog('delete')}
                      disabled={actionInProgress}
                      data-testid="user-detail-page__delete-button"
                    >
                      <FaTrashAlt /> ユーザーを削除
                    </button>
                  ) : (
                    <button 
                      className={styles['user-detail-page-restore-button']}
                      onClick={() => openConfirmDialog('restore')}
                      disabled={actionInProgress}
                      data-testid="user-detail-page__restore-button"
                    >
                      <FaUndoAlt /> ユーザーを復元
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* モーダルをPortalでレンダリング */}
      <ConfirmModal />
    </div>
  );
};

export default UserDetail; 
